import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getShops, getFactories, updateShop, updateFactory } from '../services/api';
import toast from 'react-hot-toast';

// Contact directory for shops and factories. Everyone can read it; only
// admins get the edit button. Editing writes straight back to the shop or
// factory record, so there's no separate copy of the details to keep in sync.
export default function Contacts() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';

  const [shops, setShops] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState(null); // { kind: 'shop'|'factory', item }
  const [form, setForm] = useState({ phone: '', manager_name: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [sRes, fRes] = await Promise.all([getShops(false), getFactories(false)]);
      setShops(sRes.data);
      setFactories(fRes.data);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.error || 'Σφάλμα φόρτωσης');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (kind, item) => {
    setEditing({ kind, item });
    setForm({
      phone: item.phone || '',
      manager_name: item.manager_name || '',
      address: item.address || '',
      notes: item.notes || '',
    });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        phone: form.phone.trim() || null,
        manager_name: form.manager_name.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (editing.kind === 'shop') await updateShop(editing.item.id, payload);
      else await updateFactory(editing.item.id, payload);
      toast.success('Αποθηκεύτηκε');
      setEditing(null);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  const normalize = (s) => (s || '').toString().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '');
  const match = (x) => {
    if (!search.trim()) return true;
    const q = normalize(search);
    return normalize(x.name).includes(q)
      || normalize(x.manager_name).includes(q)
      || normalize(x.phone).includes(q);
  };

  const Row = ({ kind, item }) => (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '12px 14px',
      border: '1px solid var(--gray-100)', borderRadius: 8,
      background: 'white',
    }}>
      <div style={{ fontSize: 20, lineHeight: 1.2 }}>{kind === 'shop' ? '🏪' : '🏭'}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: 'var(--gray-900)' }}>{item.name}</div>
        <div style={{ fontSize: 13, color: 'var(--gray-600)', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div>
            📞 {item.phone
              ? <a href={`tel:${item.phone.replace(/\s/g, '')}`} style={{ color: 'var(--blue-dark)' }}>{item.phone}</a>
              : <span style={{ color: 'var(--gray-400)' }}>—</span>}
          </div>
          <div>
            🧑 Υπεύθυνος/η: {item.manager_name || <span style={{ color: 'var(--gray-400)' }}>—</span>}
          </div>
          {item.address && <div>📍 {item.address}</div>}
          {item.notes && (
            <div style={{ marginTop: 4, padding: '6px 10px', background: 'var(--gray-50)', borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap' }}>
              📝 {item.notes}
            </div>
          )}
        </div>
      </div>
      {isAdmin && (
        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(kind, item)}>✏️</button>
      )}
    </div>
  );

  const visibleShops = shops.filter(match);
  const visibleFactories = factories.filter(match);

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>📇 Στοιχεία Επικοινωνίας</h2>
            <p>Τηλέφωνα και υπεύθυνοι καταστημάτων και εργοστασίων</p>
          </div>
          <div className="flex-row">
            <input
              className="form-input"
              placeholder="🔍 Όνομα, υπεύθυνος, τηλέφωνο..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 240, padding: '6px 10px', margin: 0 }}
            />
            <button className="btn btn-secondary" onClick={() => load()} title="Ανανέωση">↻</button>
          </div>
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, alignItems: 'start' }}>
            <div className="card">
              <div className="card-header">
                <span className="card-title">🏪 Καταστήματα</span>
                <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{visibleShops.length}</span>
              </div>
              <div className="card-body">
                {visibleShops.length === 0 ? (
                  <div className="empty-state" style={{ padding: 18 }}><div>🏪</div><p>Καμία εγγραφή</p></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {visibleShops.map((s) => <Row key={s.id} kind="shop" item={s} />)}
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <span className="card-title">🏭 Εργοστάσια</span>
                <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{visibleFactories.length}</span>
              </div>
              <div className="card-body">
                {visibleFactories.length === 0 ? (
                  <div className="empty-state" style={{ padding: 18 }}><div>🏭</div><p>Καμία εγγραφή</p></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {visibleFactories.map((f) => <Row key={f.id} kind="factory" item={f} />)}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div onClick={() => setEditing(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontWeight: 600 }}>
                {editing.kind === 'shop' ? '🏪' : '🏭'} {editing.item.name}
              </span>
              <button onClick={() => setEditing(null)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              <div className="form-group">
                <label className="form-label">Τηλέφωνο</label>
                <input className="form-input" value={form.phone} autoFocus
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Υπεύθυνος/η</label>
                <input className="form-input" value={form.manager_name}
                  onChange={(e) => setForm((f) => ({ ...f, manager_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Διεύθυνση</label>
                <input className="form-input" value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Σημειώσεις</label>
                <textarea className="form-textarea" rows="3" value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)}>Άκυρο</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : 'Αποθήκευση'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
